import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import LandingLayout from 'layouts/LandingLayout/LandingLayout';
import Header from 'Components/Header/Header';
import Footer from 'Components/LandingPage/Footer/Footer';

import {
  TOOL_CATEGORIES,
  MCP_ENDPOINT,
  TOOL_COUNT,
  RESOURCE_COUNT,
} from 'data/mcpTools';

import './MCPLanding.scss';

const SETUP_ID = 'connect';

/**
 * Number of tools behind a capability card, derived from the generated data.
 * Most cards map 1:1 onto a category; a card may instead name its tools
 * explicitly when the capability spans categories (publishing does).
 */
const countIn = (cap) => {
  if (cap.toolNames) {
    const names = new Set(cap.toolNames);
    return TOOL_CATEGORIES.reduce(
      (n, c) => n + c.tools.filter((t) => names.has(t.name)).length,
      0
    );
  }
  const category = TOOL_CATEGORIES.find((c) => c.id === cap.id);
  return category ? category.tools.length : 0;
};

/** First sentence of a tool description, so cards stay one line-ish. */
const firstSentence = (text = '') => {
  const trimmed = text.replace(/\s+/g, ' ').trim();
  const stop = trimmed.search(/[.!?](\s|$)/);
  const sentence = stop === -1 ? trimmed : trimmed.slice(0, stop + 1);
  return sentence.length > 150 ? `${sentence.slice(0, 147)}…` : sentence;
};

const CAPABILITIES = [
  {
    id: 'training',
    title: 'Your brand, permanently loaded',
    body:
      'Voice DNA. Content strategy. Audience personas. Style guidelines. All trained on your real content. Your agent doesn\u2019t start with \u201Ctell me about your brand\u201D — it already knows. Think of it as muscle memory for your AI.',
  },
  {
    id: 'planning',
    title: 'Blank calendar to full quarter in one chat',
    body:
      'Spin up campaigns, generate briefs, ideate dozens of topics, produce headline variations. Go from \u201Cwe need a Q4 plan\u201D to a populated editorial calendar before your coffee gets cold.',
  },
  {
    id: 'content',
    title: 'First drafts that actually sound like you',
    body:
      'Your agent writes with your voice DNA loaded, not a generic model. First drafts land in your brand tone from sentence one. Check content health, revise, polish — all before you ever open the editor.',
  },
  {
    id: 'documents',
    title: 'Total recall for your content library',
    body:
      'Search, read, create, revise, organize — across every campaign. Your agent navigates your content the way you do, except it never forgets where anything is and it reads 10x faster.',
  },
  {
    id: 'intelligence',
    title: 'Built-in research superpowers',
    body:
      'Keyword research powered by Semrush. Semantic search across your own content. Find what performed, spot the gaps, discover the opportunities — without opening another tool or another tab.',
  },
  {
    id: 'publishing',
    toolNames: ['publish_document', 'get_publish_settings', 'list_integrations'],
    title: 'Ship it. Right from the chat.',
    body:
      'WordPress, Webflow, Wix, LinkedIn, X, Facebook — pick the channel, set SEO fields and scheduling, confirm, done. Draft to published in the same conversation where you wrote it.',
  },
  {
    id: 'collaboration',
    title: 'Your team, in the loop instantly',
    body:
      'Share documents, brands and campaigns with teammates. Add collaborators, check who has access. No more \u201Ccan you share that doc with me?\u201D Slack messages — just do it from the chat.',
  },
];

const STEPS = [
  {
    title: 'Add the connector in Claude',
    body:
      'In Claude on the web, open Settings → Connectors → Add custom connector and paste the Vocable MCP URL above. Claude discovers the tools automatically.',
  },
  {
    title: 'Sign in and approve access',
    body:
      'Claude sends you to Vocable to sign in with your existing account and approve the connection. Nothing is shared until you approve, and the connector only ever acts as you.',
  },
  {
    title: 'Start prompting',
    body:
      'That is the setup done. In your first session you tell the agent which brand to work on — the tools are scoped to one brand at a time — and then just describe the work you want.',
  },
];

const CLIENTS = [
  {
    name: 'Claude (web)',
    status: 'supported',
    statusLabel: 'Chat & Cowork',
    note: 'Add it once under Settings → Connectors. Works in both Chat and Cowork.',
  },
  {
    name: 'Claude Desktop',
    status: 'supported',
    statusLabel: 'Chat & Cowork',
    note:
      'Inherits from your Claude account — no separate setup. Works in both Chat and Cowork.',
  },
  {
    name: 'Claude Code',
    status: 'supported',
    statusLabel: 'Supported',
    note:
      'Add the connector URL to your MCP config. Signs in via your browser.',
  },
  {
    name: 'Cursor',
    status: 'supported',
    statusLabel: 'Supported',
    note: 'Add as an MCP server in Cursor settings. Signs in via your browser.',
  },
];

const FAQS = [
  {
    q: 'Does the connector cost extra?',
    a:
      'The connector is a new way into the Vocable account you already have. Work an agent does through it is the same work you would do in the app, and it counts against the same account limits. If you are unsure how your current plan is metered, check your plan page or ask us before you wire it into anything automated.',
  },
  {
    q: 'What can the agent actually do?',
    a:
      'Everything you can do in Vocable, scoped to the brand you make active. That includes planning campaigns, creating and editing documents, generating content in your voice, publishing to connected channels, sharing with teammates, running keyword research, training your brand voice and more — all through the tools listed on this page. It cannot access brands or content your Vocable account cannot access.',
  },
  {
    q: 'Which plan do I need?',
    a:
      'The connector uses your existing Vocable sign-in and your account\'s usual entitlements — if a feature is available to you in the app, the matching tool is available to your agent, and if it is not, the tool will tell the agent so. We are not going to quote you a tier here that might be wrong for your account; your plan page is the accurate answer.',
  },
  {
    q: 'Is this the same as the Vocable API?',
    a:
      'No. The API is for code you write. The MCP connector is for AI agents: it describes each tool, its arguments and its results in a way a model can use directly, so you can ask for an outcome in plain language instead of wiring up requests.',
  },
  {
    q: 'How do I disconnect?',
    a:
      'Remove the connector in your Claude settings, where you added it. Access ends there — no content is deleted from Vocable, and you can reconnect later by adding the same URL again.',
  },
];

const CopyEndpoint = () => {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const handleCopy = useCallback(async () => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(MCP_ENDPOINT);
      } else {
        return;
      }
      setCopied(true);
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      setCopied(false);
    }
  }, []);

  return (
    <div className="mcp-landing__chip">
      <span className="mcp-landing__chip-label">Connector URL</span>
      <code className="mcp-landing__chip-code">{MCP_ENDPOINT}</code>
      <button
        type="button"
        className="mcp-landing__chip-btn"
        onClick={handleCopy}
        aria-label={`Copy the Vocable MCP connector URL ${MCP_ENDPOINT}`}
      >
        {copied ? 'Copied' : 'Copy'}
      </button>
      <span className="mcp-landing__sr-only" role="status" aria-live="polite">
        {copied ? 'Connector URL copied to clipboard' : ''}
      </span>
    </div>
  );
};

const MCPLanding = () => (
  <LandingLayout>
    <div className="mcp-landing">
      <Header />

      <main className="mcp-landing__main">
        {/* 1. Hero */}
        <section className="mcp-landing__hero" aria-labelledby="mcp-hero-title">
          <div className="mcp-landing__inner">
            <div className="mcp-landing__topbar">
              <Link className="mcp-landing__doclink" to="/mcp/docs/overview">
                View documentation
                <span aria-hidden="true">→</span>
              </Link>
            </div>

            <p className="mcp-landing__eyebrow">
              Model Context Protocol connector
            </p>
            <h1 id="mcp-hero-title" className="mcp-landing__h1">
              Give your AI agent superpowers. {TOOL_COUNT} of them, actually.
            </h1>
            <p className="mcp-landing__lede">
              The Vocable MCP connector plugs your entire content OS straight
              into Claude. {TOOL_COUNT} tools — plan campaigns, draft in your
              trained voice DNA, manage your library, run keyword research,
              publish to six channels, coordinate your team. Your brand
              intelligence rides shotgun on every call. No context window
              gymnastics, no re-explaining your brand for the hundredth time.
              Just connect and go.
            </p>

            <CopyEndpoint />

            <div className="mcp-landing__cta-row">
              <a className="mcp-landing__btn" href={`#${SETUP_ID}`}>
                Connect to Claude
              </a>
              <Link
                className="mcp-landing__btn mcp-landing__btn--ghost"
                to="/mcp/docs/overview"
              >
                Read the docs
              </Link>
            </div>

            <ul className="mcp-landing__facts">
              <li>
                <strong>{TOOL_COUNT}</strong> tools
              </li>
              <li>
                <strong>{TOOL_CATEGORIES.length}</strong> categories
              </li>
              <li>
                <strong>{RESOURCE_COUNT}</strong> resources
              </li>
            </ul>
          </div>
        </section>

        {/* 2. What your agent can do */}
        <section
          className="mcp-landing__section"
          aria-labelledby="mcp-capabilities-title"
        >
          <div className="mcp-landing__inner">
            <h2 id="mcp-capabilities-title" className="mcp-landing__h2">
              Like hiring a content team that never sleeps
            </h2>
            <p className="mcp-landing__sub">
              {TOOL_COUNT} tools. {TOOL_CATEGORIES.length} categories. From
              shower thought to published post — one conversation, zero tab
              switching.
            </p>

            <ul className="mcp-landing__grid mcp-landing__grid--cards">
              {CAPABILITIES.map((cap) => (
                <li className="mcp-landing__card" key={cap.id}>
                  <h3 className="mcp-landing__h3">{cap.title}</h3>
                  <p>{cap.body}</p>
                  <p className="mcp-landing__card-meta">
                    {countIn(cap)} {countIn(cap) === 1 ? 'tool' : 'tools'}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* 3. How it works */}
        <section
          className="mcp-landing__section mcp-landing__section--tint"
          id={SETUP_ID}
          aria-labelledby="mcp-setup-title"
        >
          <div className="mcp-landing__inner">
            <h2 id="mcp-setup-title" className="mcp-landing__h2">
              Setup so easy it feels like cheating
            </h2>
            <p className="mcp-landing__sub">
              No API key. No npm install. No YAML config. Paste one URL, sign
              in, and {TOOL_COUNT} tools light up.{' '}
              <Link to="/mcp/docs/quickstart">
                Full walkthrough
              </Link>
              .
            </p>

            <div className="mcp-landing__chip-wrap">
              <CopyEndpoint />
            </div>

            <ol className="mcp-landing__steps">
              {STEPS.map((step, i) => (
                <li className="mcp-landing__step" key={step.title}>
                  <span className="mcp-landing__step-num" aria-hidden="true">
                    {i + 1}
                  </span>
                  <div>
                    <h3 className="mcp-landing__h3">{step.title}</h3>
                    <p>{step.body}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* 4. Tool overview */}
        <section
          className="mcp-landing__section"
          aria-labelledby="mcp-tools-title"
        >
          <div className="mcp-landing__inner">
            <h2 id="mcp-tools-title" className="mcp-landing__h2">
              The full arsenal
            </h2>
            <p className="mcp-landing__sub">
              {TOOL_COUNT} tools, auto-generated from the live server. What you
              see here is exactly what Claude sees when you connect. No more, no
              less.
            </p>

            <div className="mcp-landing__grid mcp-landing__grid--tools">
              {TOOL_CATEGORIES.map((category) => (
                <section
                  className="mcp-landing__toolcat"
                  key={category.id}
                  aria-labelledby={`mcp-cat-${category.id}`}
                >
                  <h3 id={`mcp-cat-${category.id}`} className="mcp-landing__h3">
                    {category.label}
                    <span className="mcp-landing__pill">
                      {category.tools.length}
                    </span>
                  </h3>
                  <p className="mcp-landing__toolcat-blurb">{category.blurb}</p>
                  <dl className="mcp-landing__toollist">
                    {category.tools.map((tool) => (
                      <React.Fragment key={tool.name}>
                        <dt>
                          <code>{tool.name}</code>
                        </dt>
                        <dd>{firstSentence(tool.description)}</dd>
                      </React.Fragment>
                    ))}
                  </dl>
                </section>
              ))}
            </div>

            <p className="mcp-landing__more">
              <Link to="/mcp/docs/tools">
                Full tool reference, with every argument →
              </Link>
            </p>
          </div>
        </section>

        {/* 5. Client support */}
        <section
          className="mcp-landing__section mcp-landing__section--tint"
          aria-labelledby="mcp-clients-title"
        >
          <div className="mcp-landing__inner">
            <h2 id="mcp-clients-title" className="mcp-landing__h2">
              Where it works
            </h2>
            <p className="mcp-landing__sub">
              The connector works across the Claude ecosystem &mdash; web, desktop,
              and code. Claude Code and Cursor connect via your browser for
              sign-in, then the MCP is live in your terminal. One connector,
              every surface.
            </p>

            <ul className="mcp-landing__clients">
              {CLIENTS.map((client) => (
                <li
                  className={`mcp-landing__client mcp-landing__client--${client.status}`}
                  key={client.name}
                >
                  <h3 className="mcp-landing__h3">{client.name}</h3>
                  <p className="mcp-landing__badge">{client.statusLabel}</p>
                  <p>{client.note}</p>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* 6. FAQ */}
        <section className="mcp-landing__section" aria-labelledby="mcp-faq-title">
          <div className="mcp-landing__inner mcp-landing__inner--narrow">
            <h2 id="mcp-faq-title" className="mcp-landing__h2">
              Questions
            </h2>

            <div className="mcp-landing__faq">
              {FAQS.map((item) => (
                <details className="mcp-landing__faq-item" key={item.q}>
                  <summary>
                    <span className="mcp-landing__faq-q">{item.q}</span>
                    <span className="mcp-landing__faq-icon" aria-hidden="true" />
                  </summary>
                  <div className="mcp-landing__faq-a">
                    <p>{item.a}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* 7. Closing CTA */}
        <section
          className="mcp-landing__section mcp-landing__closing"
          aria-labelledby="mcp-closing-title"
        >
          <div className="mcp-landing__inner mcp-landing__inner--narrow">
            <h2 id="mcp-closing-title" className="mcp-landing__h2">
              Your content OS just got an AI co-pilot
            </h2>
            <p className="mcp-landing__sub">
              {TOOL_COUNT} tools. Your brand intelligence. Every channel. One
              conversation. This is what content ops looks like when AI actually
              has the full picture.
            </p>
            <div className="mcp-landing__cta-row mcp-landing__cta-row--center">
              <Link className="mcp-landing__btn" to="/mcp/docs/overview">
                Read the docs
              </Link>
              <Link
                className="mcp-landing__btn mcp-landing__btn--ghost"
                to="/mcp/docs/tools"
              >
                Browse the tools
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  </LandingLayout>
);

export default MCPLanding;
